import React from 'react';
import { useTranslation } from 'react-i18next';

export const TONES = {
    amber: { bg: '#F59E0B', fg: '#FFFFFF', ring: 'rgba(245, 158, 11, 0.35)' },
    green: { bg: '#10B981', fg: '#FFFFFF', ring: 'rgba(16, 185, 129, 0.35)' },
    blue: { bg: '#3B82F6', fg: '#FFFFFF', ring: 'rgba(59, 130, 246, 0.35)' },
    orange: { bg: '#F97316', fg: '#FFFFFF', ring: 'rgba(249, 115, 22, 0.35)' },
    slate: { bg: '#64748B', fg: '#FFFFFF', ring: 'rgba(100, 116, 139, 0.3)' },
    violet: { bg: '#8B5CF6', fg: '#FFFFFF', ring: 'rgba(139, 92, 246, 0.35)' },
    yellow: { bg: '#EAB308', fg: '#422006', ring: 'rgba(234, 179, 8, 0.4)' },
    brand: { bg: '#1D2C7B', fg: '#FFFFFF', ring: 'rgba(29, 44, 123, 0.35)' },
    red: { bg: '#EF4444', fg: '#FFFFFF', ring: 'rgba(239, 68, 68, 0.35)' },
    muted: { bg: '#CBD5E1', fg: '#475569', ring: 'rgba(148, 163, 184, 0.35)' },
};

/** Solid colour pill used for every status across the app. */
export default function StatusBadge({ tone = 'muted', children, className = '' }) {
    const style = TONES[tone] ?? TONES.muted;

    return (
        <span
            className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold leading-tight whitespace-nowrap ${className}`}
            style={{
                backgroundColor: style.bg,
                color: style.fg,
                boxShadow: `0 0 0 1px ${style.ring}, 0 1px 2px rgba(15, 23, 42, 0.06)`,
            }}
        >
            <span
                className="w-1.5 h-1.5 rounded-full shrink-0 opacity-90"
                style={{ backgroundColor: style.fg }}
            />
            {children}
        </span>
    );
}

const VISIT_STATUS = {
    queue: { tone: 'amber', key: 'status.queue' },
    examined: { tone: 'green', key: 'status.examined' },
    new: { tone: 'blue', key: 'status.new' },
    pending: { tone: 'orange', key: 'status.pending' },
    payed: { tone: 'violet', key: 'status.payed' },
    revisit: { tone: 'yellow', key: 'status.revisit' },
    closed: { tone: 'slate', key: 'status.closed' },
    null: { tone: 'muted', key: 'status.inactive' },
};

/** Badge for a visit status object or a plain status string. */
export function VisitStatusBadge({ status }) {
    const { t } = useTranslation();
    const key = typeof status === 'string' ? status : status?.status;

    if (!key) return <span className="text-xs text-slate-400">{t('common.dash')}</span>;

    const meta = VISIT_STATUS[key] ?? { tone: 'muted', key: null };

    return <StatusBadge tone={meta.tone}>{meta.key ? t(meta.key) : key}</StatusBadge>;
}

const TECH_REQUEST_STATUS = {
    pending: { tone: 'orange', key: 'techRequest.pending' },
    in_progress: { tone: 'blue', key: 'techRequest.inProgress' },
    completed: { tone: 'green', key: 'techRequest.completed' },
    returned: { tone: 'red', key: 'techRequest.returned' },
};

export function TechRequestStatusBadge({ status }) {
    const { t } = useTranslation();
    if (!status) return <span className="text-xs text-slate-400">{t('common.dash')}</span>;
    const meta = TECH_REQUEST_STATUS[status] ?? { tone: 'muted', key: null };
    return <StatusBadge tone={meta.tone}>{meta.key ? t(meta.key) : status}</StatusBadge>;
}
